import * as turf from "@turf/turf";
import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  LineString,
  Point,
  Polygon,
} from "geojson";

// Deterministic random function based on seed
function seededRandom(seed: number): number {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// Generate deterministic random number from coordinates
function coordBasedRandom(lng: number, lat: number, index = 0): number {
  const seed = Math.floor(lng * 1000000) + Math.floor(lat * 1000000) + index;
  return seededRandom(seed);
}

// Helper function to process line strings
function lineToPoints(
  line: LineString,
  spacing: number,
): Array<Feature<Point, GeoJsonProperties>> {
  const points: Array<Feature<Point, GeoJsonProperties>> = [];
  const lineString = turf.lineString(line.coordinates);
  const length = turf.length(lineString);

  // Use much tighter spacing for denser points
  const tighterSpacing = spacing * 0.3;
  const count = Math.floor(length / tighterSpacing);

  for (let i = 0; i <= count; i++) {
    // Add deterministic randomness to the distance along the line
    const baseDistance = i * tighterSpacing;
    const randomSeed = line.coordinates[0][0] + line.coordinates[0][1] + i;
    const randomOffset = (seededRandom(randomSeed) - 0.5) * tighterSpacing;
    const distance = Math.max(0, Math.min(length, baseDistance + randomOffset));

    const basePoint = turf.along(lineString, distance);
    const coord = basePoint.geometry.coordinates;

    // Add deterministic perpendicular offset for more organic variation
    const bearing = turf.bearing(
      turf.along(lineString, Math.max(0, distance - 0.001)),
      turf.along(lineString, Math.min(length, distance + 0.001)),
    );
    const perpendicularBearing = bearing + 90;
    const maxOffset = tighterSpacing; // Use full spacing as max offset
    const randomPerpendicularDistance =
      (seededRandom(randomSeed + 1000) - 0.5) * maxOffset;

    let finalCoord = coord;
    if (Math.abs(randomPerpendicularDistance) > 0.001) {
      const offsetPoint = turf.destination(
        basePoint,
        Math.abs(randomPerpendicularDistance),
        perpendicularBearing,
        { units: "kilometers" },
      );
      finalCoord = offsetPoint.geometry.coordinates;
    }

    points.push({
      type: "Feature",
      properties: {
        _fromLine: true,
      },
      geometry: {
        type: "Point",
        coordinates: finalCoord,
      },
    });
  }
  return points;
}

// Helper function to process polygons
function polygonToPoints(
  polygon: Polygon,
  spacing: number,
): Array<Feature<Point, GeoJsonProperties>> {
  // Generate a bounding box for the polygon
  const bbox = turf.bbox(polygon);

  // Use tighter spacing for more organic look
  const tighterSpacing = spacing * 0.7;

  // Generate a grid of points with the tighter spacing inside this bounding box
  const pointGrid = turf.pointGrid(bbox, tighterSpacing);

  // Filter the grid of points to keep only those that fall inside the polygon
  const pointsInPolygon = pointGrid.features.filter((point) => {
    return turf.booleanPointInPolygon(point, polygon);
  });

  // Add deterministic randomness to make it more organic
  const organicPoints = pointsInPolygon.map((point) => {
    // Add deterministic random offset to each point (up to full spacing)
    const [lng, lat] = point.geometry.coordinates;
    const maxOffset = tighterSpacing; // Use full spacing as max offset
    const randomOffsetX = (coordBasedRandom(lng, lat, 0) - 0.5) * maxOffset;
    const randomOffsetY = (coordBasedRandom(lng, lat, 1) - 0.5) * maxOffset;

    // Convert offset to lat/lng (approximate)
    const newLng = lng + randomOffsetX / 111; // Rough conversion: 1 degree ≈ 111km
    const newLat = lat + randomOffsetY / 111;

    const offsetPoint = {
      type: "Feature" as const,
      properties: {},
      geometry: {
        type: "Point" as const,
        coordinates: [newLng, newLat],
      },
    };

    // Only keep the offset point if it's still inside the polygon
    const isStillInside = turf.booleanPointInPolygon(offsetPoint, polygon);

    return {
      ...point,
      geometry: {
        type: "Point" as const,
        coordinates: isStillInside
          ? [newLng, newLat]
          : point.geometry.coordinates,
      },
      properties: {
        ...point.properties,
        _fromPolygon: true,
      },
    };
  });

  // Add some additional scattered points for more organic distribution
  const additionalPointsCount = Math.min(
    50,
    Math.floor(organicPoints.length * 0.2),
  );
  const additionalPoints: Array<Feature<Point, GeoJsonProperties>> = [];

  // Use polygon's first coordinate as base seed for deterministic additional points
  const baseSeed = polygon.coordinates[0][0][0] + polygon.coordinates[0][0][1];

  for (let i = 0; i < additionalPointsCount; i++) {
    // Generate deterministic random points within the polygon bounds
    const randomLng =
      bbox[0] + seededRandom(baseSeed + i) * (bbox[2] - bbox[0]);
    const randomLat =
      bbox[1] + seededRandom(baseSeed + i + 1000) * (bbox[3] - bbox[1]);

    const randomPoint = {
      type: "Feature" as const,
      properties: { _fromPolygon: true },
      geometry: {
        type: "Point" as const,
        coordinates: [randomLng, randomLat],
      },
    };

    // Only add if it's inside the polygon
    if (turf.booleanPointInPolygon(randomPoint, polygon)) {
      additionalPoints.push(randomPoint);
    }
  }

  const allPoints = [...organicPoints, ...additionalPoints];

  // Ensure we have at least some points for the heatmap
  if (allPoints.length === 0) {
    console.warn(
      "No points generated for polygon, using polygon vertices as fallback",
    );
    // If no points were generated, use the polygon vertices as a fallback
    const vertices: Array<Feature<Point, GeoJsonProperties>> =
      polygon.coordinates[0].map((coord) => ({
        type: "Feature" as const,
        properties: {
          _fromPolygon: true,
        },
        geometry: {
          type: "Point" as const,
          coordinates: coord,
        },
      }));
    return vertices;
  }

  return allPoints;
}

// Helper function to chunk an array
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  const results: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    results.push(array.slice(i, i + chunkSize));
  }
  return results;
}

// Function to estimate total points
function estimateTotalPoints(
  geoData: FeatureCollection,
  dynamicAreaSpacing: number,
  dynamicPathSpacing: number,
): number {
  let estimatedPoints = 0;
  for (const feature of geoData.features) {
    if (feature.geometry.type === "Polygon") {
      const polygon = feature.geometry as Polygon;
      const bbox = turf.bbox(polygon);
      const pointGrid = turf.pointGrid(bbox, dynamicAreaSpacing);
      estimatedPoints += pointGrid.features.filter((point) => {
        return turf.booleanPointInPolygon(point, polygon);
      }).length;
    } else if (feature.geometry.type === "LineString") {
      const line = feature.geometry as LineString;
      const lineString = turf.lineString(line.coordinates);
      const length = turf.length(lineString);
      estimatedPoints += Math.floor(length / dynamicPathSpacing);
    }
  }
  return estimatedPoints;
}

// Main function to flatten features into points
export const flattenToPoints = (
  mapbounds: FeatureCollection,
  geoData: FeatureCollection,
  maxPoints = 100000, // Maximum number of points
): FeatureCollection => {
  // Get the bounding box of the entire map bounds
  const mapboundsBbox = turf.bbox(mapbounds);

  // Get the area of the bounding box of the map bounds
  const mapboundsBboxPolygon = turf.bboxPolygon(mapboundsBbox);
  const mapboundsArea = turf.area(mapboundsBboxPolygon);

  // Calculate an initial dynamic spacing based on the area and the number of features
  const entryLength = geoData?.features?.length;
  const factor =
    entryLength < 11
      ? 1
      : entryLength > 11
        ? 2
        : entryLength > 100
          ? 3
          : entryLength > 500
            ? 4
            : entryLength > 1000
              ? 5
              : entryLength > 1500
                ? 6
                : entryLength > 2000
                  ? 7
                  : 8;

  // Set the dynamic spacing based on the map bounds area
  let dynamicAreaSpacing = Math.sqrt(mapboundsArea) / (200000 / factor);
  let dynamicPathSpacing = Math.sqrt(mapboundsArea) / (500000 / factor);

  // Ensure minimum spacing of 0.03
  if (dynamicAreaSpacing < 0.03) {
    dynamicAreaSpacing = 0.03;
  }
  if (dynamicPathSpacing < 0.03) {
    dynamicPathSpacing = 0.03;
  }

  // Estimate the total number of points
  const estimatedPoints = estimateTotalPoints(
    geoData,
    dynamicAreaSpacing,
    dynamicPathSpacing,
  );

  // Adjust spacing if estimated points exceed maxPoints
  if (estimatedPoints > maxPoints) {
    const adjustmentFactor = Math.sqrt(estimatedPoints / maxPoints);
    dynamicAreaSpacing *= adjustmentFactor;
    dynamicPathSpacing *= adjustmentFactor;
    console.log("Adjusted Spacings", dynamicAreaSpacing, dynamicPathSpacing);
  }

  // Calculate points for each feature type with adjusted spacing
  const finalPoints: Array<Feature<Point, GeoJsonProperties>> = [];

  // Log the number of features by type
  const featureTypes = geoData.features.reduce((acc, feature) => {
    if (!feature.geometry || !feature.geometry.type) return acc;
    acc[feature.geometry.type] = (acc[feature.geometry.type] || 0) + 1;
    return acc;
  }, {});

  for (const feature of geoData.features) {
    if (!feature.geometry || !feature.geometry.type) {
      console.error("Invalid geometry:", feature);
      continue;
    }

    switch (feature.geometry.type) {
      case "Point":
        // Ensure properties are not null
        finalPoints.push({
          type: "Feature",
          properties: feature.properties || {},
          geometry: feature.geometry,
        });
        break;
      case "LineString":
        finalPoints.push(
          ...lineToPoints(feature.geometry as LineString, dynamicPathSpacing),
        );
        break;
      case "Polygon":
        finalPoints.push(
          ...polygonToPoints(feature.geometry as Polygon, dynamicAreaSpacing),
        );
        break;
      default:
        console.warn(`Unsupported geometry type: ${feature.geometry.type}`);
        break;
    }
  }

  // If the number of points still exceeds maxPoints, adjust again
  if (finalPoints.length > maxPoints) {
    const adjustmentFactor = Math.sqrt(finalPoints.length / maxPoints);
    dynamicAreaSpacing *= adjustmentFactor;
    dynamicPathSpacing *= adjustmentFactor;

    // Recalculate points with final adjusted spacing
    const adjustedPoints: Array<Feature<Point, GeoJsonProperties>> = [];
    for (const feature of geoData.features) {
      if (!feature || !feature.geometry || !feature.geometry.type) {
        console.error("Invalid geometry:", feature);
        continue;
      }

      switch (feature.geometry.type) {
        case "Point":
          // Ensure properties are not null
          adjustedPoints.push({
            type: "Feature",
            properties: feature.properties || {},
            geometry: feature.geometry,
          });
          break;
        case "LineString":
          adjustedPoints.push(
            ...lineToPoints(feature.geometry as LineString, dynamicPathSpacing),
          );
          break;
        case "Polygon":
          adjustedPoints.push(
            ...polygonToPoints(feature.geometry as Polygon, dynamicAreaSpacing),
          );
          break;
        default:
          console.warn(`Unsupported geometry type: ${feature.geometry.type}`);
          break;
      }
    }

    return {
      type: "FeatureCollection",
      features: adjustedPoints,
    };
  }

  return {
    type: "FeatureCollection",
    features: finalPoints,
  };
};
